import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { cn } from "../../utils/cn";
import { FOLDER_COLOR_STYLES, DEFAULT_FOLDER_COLOR } from "./folderColors";

// ─── Selector "Mover a carpeta" ──────────────────────────────────────────────
// Popover con la lista de carpetas del tenant (≤30 por contrato: no necesita
// buscador). Mismo posicionamiento/estilo que el menú overflow del detalle.
// "Sin carpeta" va arriba solo cuando la conversación YA tiene carpeta: si no
// la tiene, esa opción sería un no-op confuso.
export default function FolderPicker({ folders = [], currentFolderId = null, onPick, onClose }) {
  const { t } = useTranslation();
  const rootRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      onClose?.();
    };
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  // Foco inicial en la carpeta actual (o el primer ítem): el popover se abre
  // desde un menú que ya se cerró, así que sin esto el foco cae al <body>.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const current = root.querySelector('[data-current="true"]');
    const target = current || root.querySelector("button");
    target?.focus?.();
  }, []);

  const hasFolder = !!currentFolderId;

  return (
    <div
      ref={rootRef}
      role="menu"
      aria-label={t("Mover a carpeta")}
      className="absolute right-0 top-full mt-1 min-w-[200px] max-w-[260px] z-30 bg-bg border border-border rounded-md shadow-elevated py-1 animate-scale-in origin-top-right motion-reduce:animate-none"
    >
      {hasFolder && (
        <>
          <button
            type="button"
            role="menuitem"
            onClick={() => onPick?.(null)}
            className="w-full flex items-center gap-2 px-3 h-8 text-[12px] text-fg hover:bg-bg-muted text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset"
          >
            {t("Sin carpeta")}
          </button>
          <div className="my-1 border-t border-border/60" aria-hidden="true" />
        </>
      )}
      {folders.length === 0 ? (
        <div className="px-3 py-2 text-[11px] text-fg-muted">
          {t("Aún no hay carpetas. Créalas desde la barra lateral.")}
        </div>
      ) : (
        folders.map((folder) => {
          const isCurrent = folder.id === currentFolderId;
          const dotClass =
            (FOLDER_COLOR_STYLES[folder.color] || FOLDER_COLOR_STYLES[DEFAULT_FOLDER_COLOR]).dot;
          return (
            <button
              key={folder.id}
              type="button"
              role="menuitem"
              data-current={isCurrent ? "true" : undefined}
              onClick={() => onPick?.(folder.id)}
              className={cn(
                "w-full flex items-center gap-2 px-3 h-8 text-[12px] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset",
                isCurrent ? "text-fg font-medium bg-bg-muted/60" : "text-fg hover:bg-bg-muted",
              )}
            >
              <span className={cn("w-2 h-2 rounded-full shrink-0", dotClass)} aria-hidden="true" />
              <span className="flex-1 truncate">{folder.name}</span>
              {isCurrent && (
                <Check className="w-3.5 h-3.5 text-brand-600 shrink-0" aria-hidden="true" />
              )}
            </button>
          );
        })
      )}
    </div>
  );
}
